import { useState, useCallback, useEffect } from 'react';
import loadable from '@loadable/component';
import { routes } from '@/components/DeprecatedPortfolio/redux/constants/routes';
import { useTracking } from 'react-tracking';
import MobileTopbarSubNav, { ListItem } from '@/components/MobileTopbarSubNav';
import DesktopSidebarWithSnowflake from './components/DesktopSidebarWithSnowflake';
import { TwoColumnWithDesktopSidebar } from '@/components/Layout';
import DocumentHeader from '@/components/DocumentHeader';
import ContextProvider, { SWSHighCharts } from '@/context/HighChartsProvider';
import Highcharts from 'highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import tree from 'highcharts/modules/treemap';
import heatmap from 'highcharts/modules/heatmap';
import patternFill from 'thirdparty/highcharts/pattern-fill-v2';
import Theme from '@/components/charts/themes/sws';
import {
  Button,
  ButtonWithDropdown,
  useMount,
  useTheme,
} from '@simplywallst/ui-core';
import DownloadPdfSolid from '@simplywallst/ui-core/icons/DownloadPdfSolid';
import DownloadReportSolid from '@simplywallst/ui-core/icons/DownloadReportSolid';
import { useIntersectionListener } from '@/hooks/useIntersectionListener';
import LoadingOverlay from '@/components/LoadingOverlay';
import { useStream } from '@/components/Stream/hooks/useStream';
import { useUserAccess } from '@/hooks/useUserAccess';
import type { CurrencyISO } from '@/constants/currencies';

const PortfolioReport = loadable(() => import('./components/PortfolioReport'));

export interface Props {
  id: string | null;
  isEditable?: boolean;
  name: string | null;
  provider?: string | null;
  currentHoldingCount: number;
  score: number[];
  totalValueFormatted: string;
  currencyISO?: CurrencyISO;
  isWatchlist: boolean;
  secretCode?: string | null;
  loadingCsvReport: boolean;
  redirectToEditTransactions: (args: { id: string; name: string }) => void;
  handleFetchCompanyCsvReport: (portfolioId: string) => void;
  handleShowPlans: () => void;
  handlePortfolioRefresh: (id: string) => void;
  handleDeletePortfolio: (args: {
    id: string;
    redirectToList: boolean;
  }) => void;
  handleShowEditPortfolio: () => void;
}

const Portfolio = ({
  currencyISO,
  currentHoldingCount,
  id,
  isEditable,
  isWatchlist,
  name,
  provider,
  score,
  totalValueFormatted,
  loadingCsvReport,
  secretCode,
  handleFetchCompanyCsvReport,
  handleShowPlans,
  redirectToEditTransactions,
  handlePortfolioRefresh,
  handleShowEditPortfolio,
  handleDeletePortfolio,
}: Props) => {
  useStream();
  const { data: userAccessData } = useUserAccess();

  const { Track, trackEvent } = useTracking({
    subject: 'page',
    type: 'track',
    portfolioId: id,
  });
  const [hcReady, setHcReady] = useState(false);
  const theme = useTheme();

  useMount(() => {
    trackEvent({
      subject: 'page',
      action: 'landed',
    });
  });

  useEffect(() => {
    const TypedHighcharts = Highcharts as any;
    if (typeof TypedHighcharts === 'object' && !hcReady) {
      const themeOverrides = (highChartTheme) => {
        return {
          ...highChartTheme,
          textColor: theme.color.white,
          legend: {
            itemStyle: {
              color: theme.color.white,
            },
          },
          yAxis: {
            title: {
              style: {
                color: theme.color.white,
              },
            },
            labels: {
              style: {
                color: theme.color.white,
              },
            },
          },
          xAxis: {
            labels: {
              style: {
                color: theme.color.white,
              },
            },
          },
        };
      };
      highchartsMore(TypedHighcharts);
      tree(TypedHighcharts);
      heatmap(TypedHighcharts);
      patternFill(TypedHighcharts);
      // Apply the theme
      TypedHighcharts.theme = Theme(TypedHighcharts);
      TypedHighcharts.theme = themeOverrides(TypedHighcharts.theme);
      TypedHighcharts.setOptions(TypedHighcharts.theme);
      setHcReady(true);
      console.log('Loaded High charts more..');
    }
  }, [hcReady, theme]);

  const handleDownloadCsvFile = useCallback(() => {
    trackEvent({
      action: 'click',
      subject: 'Download Portfolio CSV',
    });

    if (userAccessData.canGetPortfolioReportCsv && id) {
      handleFetchCompanyCsvReport(id);
    } else {
      handleShowPlans();
    }
  }, [
    id,
    handleFetchCompanyCsvReport,
    handleShowPlans,
    userAccessData.canGetPortfolioReportCsv,
    trackEvent,
  ]);

  const { selected, handleIntersectionChange } = useIntersectionListener();

  if (name === '' || score.length === 0) {
    return <LoadingOverlay />;
  }

  return (
    <Track>
      <ContextProvider highCharts={Highcharts as SWSHighCharts}>
        <TwoColumnWithDesktopSidebar>
          <DocumentHeader title={name} />
          <MobileTopbarSubNav routes={routes} selected={selected}>
            {({ routes, handleClick, refMap, selected }) =>
              routes.map(({ anchor, label, tracking }, i) => (
                <ListItem
                  ref={refMap[i]}
                  key={`MobileTopbarListItem-${anchor}-${i}`}
                  selected={selected === anchor}
                >
                  <Button
                    as="a"
                    href={`#${anchor}`}
                    onClick={() => handleClick(tracking || label, anchor)}
                  >
                    {label}
                  </Button>
                </ListItem>
              ))
            }
          </MobileTopbarSubNav>
          <DesktopSidebarWithSnowflake
            score={score}
            selected={selected}
            title={name}
            routes={routes}
            subTitle={`${isWatchlist ? `Watchlist` : `Portfolio`} Analysis`}
          >
            {loadingCsvReport === false && (
              <ButtonWithDropdown
                styleType="gentle"
                onSelect={handleDownloadCsvFile}
                floatLeft
                options={[
                  {
                    label: 'Download Portfolio Report Data',
                    value: 'CSV',
                    icon: <DownloadPdfSolid />,
                  },
                ]}
              >
                <DownloadReportSolid />
              </ButtonWithDropdown>
            )}
          </DesktopSidebarWithSnowflake>
          <PortfolioReport
            id={id}
            isEditable={isEditable}
            name={name}
            provider={provider}
            redirectToEditTransactions={redirectToEditTransactions}
            totalValueFormatted={totalValueFormatted}
            currencyISO={currencyISO}
            currentHoldingCount={currentHoldingCount}
            isWatchlist={isWatchlist}
            secretCode={secretCode}
            onIntersectionChange={handleIntersectionChange}
            handlePortfolioRefresh={handlePortfolioRefresh}
            handleShowEditPortfolio={handleShowEditPortfolio}
            handleDeletePortfolio={handleDeletePortfolio}
          />
        </TwoColumnWithDesktopSidebar>
      </ContextProvider>
    </Track>
  );
};

export default Portfolio;
